import React, { useRef, useState, useEffect } from 'react';
import {
    Button,
    Typography,
    Box,
    TextField,
    CircularProgress
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';

export const FileManager = ({
    mode,
    filesList,
    error,
    downloadKey,
    setDownloadKey,
    uploadKey,
    setUploadKey,
    uploadFile,
    setUploadFile,
    fetchFileList,
    downloadFileFromS3,
    uploadFileToS3,
    deleteFileFromS3
}) => {
    const fileInputRef = useRef(null);
    const [isListingFiles, setIsListingFiles] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [localFilesList, setLocalFilesList] = useState(filesList);
    const [selectedFileName, setSelectedFileName] = useState('');

    // Add useEffect to update local state when filesList changes
    useEffect(() => {
        setLocalFilesList(filesList);
        console.log('FilesList updated:', filesList);
    }, [filesList]);

    // Function to transform flat file list into tree structure
    const transformFilesToTreeData = (files) => {
        const treeData = [];
        const folderMap = new Map();

        // First pass: Create folders
        files.forEach(file => {
            const parts = file.split('/');

            // If it's a folder or has nested path
            if (parts.length > 1) {
                let currentPath = '';
                parts.slice(0, -1).forEach((folder, index) => {
                    currentPath += (index > 0 ? '/' : '') + folder;

                    if (!folderMap.has(currentPath)) {
                        const folderItem = {
                            id: currentPath,
                            label: folder,
                            children: []
                        };
                        folderMap.set(currentPath, folderItem);

                        // Add to parent folder or root
                        const parentPath = parts.slice(0, index).join('/');
                        if (parentPath && folderMap.has(parentPath)) {
                            folderMap.get(parentPath).children.push(folderItem);
                        } else {
                            treeData.push(folderItem);
                        }
                    }
                });
            }
        });

        // Second pass: Add files to their respective folders
        files.forEach(file => {
            const parts = file.split('/');

            if (parts.length > 1) {
                const folderPath = parts.slice(0, -1).join('/');
                const parentFolder = folderMap.get(folderPath);

                if (parentFolder) {
                    parentFolder.children.push({
                        id: file,
                        label: parts[parts.length - 1]
                    });
                }
            } else {
                // Root level files
                treeData.push({
                    id: file,
                    label: file
                });
            }
        });
        return treeData;
    };

    const handleTreeItemSelection = (event, itemId) => {
        // Find the item in the tree data
        const findItemById = (items) => {
            for (const item of items) {
                if (item.id === itemId) return item;
                if (item.children) {
                    const found = findItemById(item.children);
                    if (found) return found;
                }
            }
            return null;
        };

        const selectedItem = findItemById(transformFilesToTreeData(localFilesList.files));

        // Check if the item has children (is a folder)
        if (selectedItem && selectedItem.children && selectedItem.children.length > 0) {
            // If it's a folder, ensure it ends with a slash
            setDownloadKey(itemId.endsWith('/') ? itemId : `${itemId}/`);
        } else {
            // If it's a file, set the full path
            setDownloadKey(itemId);
        }
    };

    const handleFetchFileList = async () => {
        setIsListingFiles(true);
        try {
            await fetchFileList();
            // Optional: Add any additional logging or state management
            console.log('Fetch file list completed');
        } catch (err) {
            console.error('Error fetching file list:', err);
        } finally {
            setIsListingFiles(false);
        }
    };

    const handleUploadFileToS3 = async () => {
        setIsUploading(true);
        try {
            await uploadFileToS3();
        } catch (err) {
            console.error('Error uploading file:', err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadFileFromS3 = async () => {
        setIsDownloading(true);
        try {
            await downloadFileFromS3();
        } catch (err) {
            console.error('Error downloading file:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDeleteFileFromS3 = async () => {
        if (!downloadKey) {
            alert("No file selected for deletion.");
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${downloadKey}?`)) {
            setIsDeleting(true);
            try {
                await deleteFileFromS3();
                await fetchFileList(); // Refresh the file list after deletion
            } catch (err) {
                console.error('Error deleting file:', err);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <Box
            sx={{
                padding: '1em',
                border: '1px solid #ccc',
                borderRadius: '15px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                backgroundColor: mode === 'light' ? '#f9f9f9' : '#424242',
                color: mode === 'light' ? '#000' : '#fff'
            }}
        >
            <Typography variant="h2" sx={{ fontSize: '1.5rem', marginBottom: '1em' }}>
                File Manager
            </Typography>

            {/* File List Section */}
            <Box sx={{ marginBottom: '2em' }}>
                <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                    List files
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleFetchFileList}
                    disabled={isListingFiles}
                    sx={{ marginBottom: '1em' }}
                >
                    {isListingFiles ? <CircularProgress size={24} /> : 'List All Files'}
                </Button>

                {localFilesList && localFilesList.files && (
                    <Box
                        key={JSON.stringify(localFilesList.files)} // Force re-render
                        sx={{
                            backgroundColor: mode === 'light' ? '#fff' : '#616161',
                            color: mode === 'light' ? '#000' : '#fff',
                            padding: '1em',
                            borderRadius: '5px',
                            maxHeight: '300px',
                            overflowY: 'auto'
                        }}
                    >
                        <RichTreeView
                            items={transformFilesToTreeData(localFilesList.files)}
                            onItemSelectionToggle={handleTreeItemSelection}
                            sx={{
                                color: mode === 'light' ? '#000' : '#fff',
                                '& .MuiTreeItem-content': {
                                    color: mode === 'light' ? '#000' : '#fff'
                                }
                            }}
                        />
                    </Box>
                )}

                {error && (
                    <Typography
                        color="error"
                        sx={{ color: mode === 'light' ? 'red' : '#ff5252' }}
                    >
                        Error: {JSON.stringify(error)}
                    </Typography>
                )}
            </Box>

            <hr />

            {/* Upload File Section */}
            <Box sx={{ marginBottom: '2em' }}>
                <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                    Upload file
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: '1em' }}>
                    Select a file using the choose file button, then put the destination file name in the text box.
                    <br />
                    To upload to a subfolder, simply put it in the text box as 'subfolder/filename.ext'
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Button
                        variant="contained"
                        component="label"
                        color="primary"
                        sx={{ height: '40px' }}
                    >
                        Choose File
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                setUploadFile(file);
                                setSelectedFileName(file ? file.name : '');
                            }}
                            hidden
                        />
                    </Button>
                    {selectedFileName && (
                        <Typography variant="body1" sx={{ marginLeft: '1em' }}>
                            Selected: {selectedFileName}
                        </Typography>
                    )}

                    <TextField
                        type="text"
                        placeholder="Enter upload key (e.g. folder/file.txt)"
                        value={uploadKey}
                        onChange={(e) => setUploadKey(e.target.value)}
                        sx={{
                            flexGrow: 1,
                            '& .MuiInputBase-root': {
                                backgroundColor: mode === 'light' ? '#fff' : '#616161',
                                color: mode === 'light' ? '#000' : '#fff'
                            }
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadFileToS3}
                        disabled={isUploading}
                        sx={{ height: '40px' }}
                    >
                        {isUploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </Box>
            </Box>

            <hr />

            {/* Download File Section */}
            <Box>
                <Typography variant="h3" sx={{ fontSize: '1.2rem', marginBottom: '1em' }}>
                    Download file
                </Typography>
                <Typography variant="body2" sx={{ marginBottom: '1em' }}>
                    To download a folder, specify the folder name, followed by the slash '/'.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <TextField
                        type="text"
                        value={downloadKey}
                        onChange={(e) => setDownloadKey(e.target.value)}
                        placeholder="Enter text..."
                        sx={{
                            flexGrow: 1,
                            '& .MuiInputBase-root': {
                                backgroundColor: mode === 'light' ? '#fff' : '#616161',
                                color: mode === 'light' ? '#000' : '#fff'
                            }
                        }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownloadFileFromS3}
                        disabled={isDownloading}
                        sx={{ height: '40px' }}
                    >
                        {isDownloading ? <CircularProgress size={24} /> : <CloudDownloadIcon />}
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteFileFromS3}
                        disabled={isDownloading || isDeleting || !downloadKey}
                        sx={{ height: '40px' }}
                    >
                        <DeleteForeverIcon />
                    </Button>
                </Box>

                {error && (
                    <Typography
                        color="error"
                        sx={{ color: mode === 'light' ? 'red' : '#ff5252', marginTop: '1em' }}
                    >
                        Error: {JSON.stringify(error)}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};